import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link as MuiLink,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import size from "../../../assets/icons/size.svg";
import lowsize from "../../../assets/icons/lowsize.svg";
import SVGViewer from "../../SVGViewer";
import BplPdfViewer from "../../PDFViewer/BplPdfViewer";
import { getLeadLabel } from "./reportUtils";

// --------------------------------------------------------------- EcgGraphView

/** Renders the ECG itself: an SVG trace, or BPL's PDF when that's what we got. */
export const EcgGraphView = ({
  ecgGraph,
  documentRef = null,
  enableZoom = true,
}) =>
  !ecgGraph?.BPLReport ? (
    <SVGViewer
      svgGraph={ecgGraph?.svgGraph}
      documentRef={documentRef}
      enableZoom={enableZoom}
    />
  ) : (
    <BplPdfViewer BPLReport={ecgGraph?.ReportPdfURL} />
  );

// ------------------------------------------------------------ ReportDeviceNote

const SPANDAN_URL = "https://www.sunfox.in/spandan";
const SPANDAN_TEL = "tel:+917060000556";
const SPANDAN_PHONE = "(+91) 7060000556";

const DeviceDetails = ({ meta }) => (
  <>
    Device Variant: <strong>{meta?.deviceName}</strong> | Source:{" "}
    <strong>{meta?.source}</strong> | App Version:{" "}
    <strong>{meta?.appVersion}</strong> | ECG Processor Version:{" "}
    <strong>{meta?.ecgProcessorVersion}</strong> | Firmware Version:{" "}
    <strong>{meta?.firmwareVersion}</strong> | Device ID:{" "}
    <strong>{meta?.deviceId}</strong>
  </>
);

/** Provenance footer shown under a graph: device info + Spandan contact. */
export const ReportDeviceNote = ({
  meta,
  variant = "caption",
  LinkComponent,
}) => {
  if (variant === "compact") {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: -3 }}>
        <small className="col-md-10" style={{ fontSize: "8px" }}>
          Note: This report is generated using{" "}
          <strong>Spandan portable ECG device.</strong> To know more about
          Spandan visit{" "}
          <LinkComponent to={SPANDAN_URL}>{SPANDAN_URL}</LinkComponent> or call
          at <LinkComponent to={SPANDAN_TEL}>{SPANDAN_PHONE}</LinkComponent>
        </small>
        <small style={{ fontSize: "8px" }}>
          <DeviceDetails meta={meta} />
        </small>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Typography variant="caption" display="block" gutterBottom>
        Note: This report is generated using{" "}
        <strong>Spandan portable ECG device.</strong> To know more about Spandan
        visit{" "}
        <MuiLink href={SPANDAN_URL} target="_blank" underline="hover">
          {SPANDAN_URL}
        </MuiLink>{" "}
        or call at{" "}
        <MuiLink href={SPANDAN_TEL} underline="hover">
          {SPANDAN_PHONE}
        </MuiLink>
      </Typography>

      <Typography variant="caption" display="block">
        <DeviceDetails meta={meta} />
      </Typography>
    </Box>
  );
};

// ------------------------------------------------------------- Graph card UI

const EXPAND_BUTTON_SX = {
  position: "absolute",
  bottom: 16,
  right: 16,
  width: 40,
  height: 40,
  bgcolor: "var(--app-surface)",
  borderRadius: "4px",
  p: 0.5,
  "&:hover": {
    bgcolor: "var(--app-surface-2)",
  },
};

const GraphSkeleton = () => (
  <Box sx={{ width: "100%", minHeight: 400 }}>
    <Skeleton
      animation="wave"
      variant="rectangular"
      width="100%"
      height={400}
      sx={{ borderRadius: 2 }}
    />
  </Box>
);

export const EcgPreviewCard = ({
  data,
  ecgGraph,
  isLoadingGraph,
  deviceMeta,
  LinkComponent,
  onExpand,
}) => (
  <Box
    className="card relative"
    sx={{ backgroundColor: "var(--app-surface-2)", padding: "15px" }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "14px",
      }}
    >
      <small>
        <strong>{getLeadLabel(data?.report_type)}</strong>
      </small>
      {data?.characteristics?.bpm && (
        <small>
          Heart Rate: <strong>{data?.characteristics?.bpm} bpm</strong>
        </small>
      )}
    </Box>

    <Box sx={{ width: "100%", height: "auto" }}>
      {isLoadingGraph ? (
        <GraphSkeleton />
      ) : (
        <EcgGraphView ecgGraph={ecgGraph} enableZoom={false} />
      )}
    </Box>

    <IconButton onClick={onExpand} sx={EXPAND_BUTTON_SX}>
      <Box
        component="img"
        src={size}
        alt="Download"
        sx={{ width: "60%", height: "60%" }}
      />
    </IconButton>

    {!ecgGraph?.BPLReport && (
      <ReportDeviceNote
        meta={deviceMeta}
        variant="compact"
        LinkComponent={LinkComponent}
      />
    )}
  </Box>
);

export const EcgGraphDialog = ({
  open,
  onClose,
  data,
  ecgGraph,
  isLoadingGraph,
  deviceMeta,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="lg"
    PaperProps={{
      sx: {
        borderRadius: 3,
        backgroundColor: "var(--app-surface)",
        position: "relative",
      },
    }}
  >
    <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography fontWeight={700} sx={{ textDecoration: "underline" }}>
          {getLeadLabel(data?.report_type)}
        </Typography>

        {data?.characteristics?.bpm && (
          <Typography variant="body2">
            Heart Rate:&nbsp;
            <strong>{data.characteristics.bpm} bpm</strong>
          </Typography>
        )}
      </Box>

      {isLoadingGraph ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 1200 }}>
            <EcgGraphView ecgGraph={ecgGraph} />
          </Box>
        </Box>
      )}

      <IconButton
        onClick={onClose}
        sx={{
          ...EXPAND_BUTTON_SX,
          boxShadow: "2px 2px 8px 2px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          component="img"
          src={lowsize}
          alt="Close"
          sx={{ width: "80%", height: "80%" }}
        />
      </IconButton>

      {!ecgGraph?.BPLReport && <ReportDeviceNote meta={deviceMeta} />}
    </DialogContent>
  </Dialog>
);

export const EcgGraphCard = ({ data, ecgGraph, isLoadingGraph }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      p: 2,
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
      sx={{ fontSize: 14 }}
    >
      <Typography variant="caption" fontWeight={700}>
        {getLeadLabel(data?.report_type)}
      </Typography>

      {data?.characteristics?.bpm && (
        <Typography variant="caption">
          Heart Rate:&nbsp;
          <Typography component="span" fontWeight={700}>
            {data.characteristics.bpm} bpm
          </Typography>
        </Typography>
      )}
    </Box>

    <Box
      sx={{
        width: "100%",
        maxHeight: "100%",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {isLoadingGraph ? (
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height="100%"
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            "& svg": { width: "100%", height: "100%" },
          }}
        >
          <EcgGraphView ecgGraph={ecgGraph} />
        </Box>
      )}
    </Box>
  </Paper>
);

export const EcgGraphSection = ({ data, ecgGraph, isLoadingGraph }) =>
  isLoadingGraph ? (
    <GraphSkeleton />
  ) : (
    <EcgGraphCard
      data={data}
      ecgGraph={ecgGraph}
      isLoadingGraph={isLoadingGraph}
    />
  );
